// src/pages/vendas/ViewNotaModal.js
import React, { useEffect, useState } from 'react';
import { formatCurrency, formatNumeric } from '../../utils/numberUtils';
import { formatDate, formatTime } from '../../utils/dateTimeUtils';
import { getDescricaoSituacaoNF } from '../../models/vendas/SituacaoNF.ts';
import { Nota } from '../../models/vendas/Nota.js';
import "./ViewNotaModal.css";
import { useNota } from '../../contexts/vendas/NotaContext.js';
import { usePrintNFCe } from '../../contexts/geral/PrintNFCeContext.js';
import ModalPortal from '../../components/modalForm/ModalPortal.js';

const ViewNotaModal = ({ show, onHide, notaId }) => {  
  // const [notaItens, setNotaItens] = useState();
  // const [notaPags, setNotaPags] = useState();
  const [nota, setNota] = useState(new Nota());
  const {buscaNotaById} = useNota();
  const { visualizarNFCePdf } = usePrintNFCe();
  
  useEffect(() => {
    const fetchItens = async () => {
      if (show && notaId) {
        const result = await buscaNotaById(notaId);
        setNota(result);      
      }      
    };

    fetchItens();
  }, [show, notaId]);

  if (!show || !notaId) return null;

  const handleVisualizarDanfeNFCe = () => {
    visualizarNFCePdf(notaId);
  };

  return (
    <ModalPortal show={show}>
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      role="dialog"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className="modal-dialog modal-lg" role="document" style={{ maxWidth: '900px' }}>
        <div className="modal-content">
          <div className="modal-header bg-info text-white">
            <h5 className="modal-title">Visualizar Nota Fiscal - {nota.numDoc}</h5>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={onHide}
            />
          </div>
          <div className="modal-body">
            {/* Informações Gerais da Nota */}
            <div className="mb-4">
              <h6>Dados da Nota</h6>
              <table className="table table-sm table-bordered">
                <tbody>
                  <tr>
                    <th>ID</th>
                    <td>{nota.id}</td>
                    <th>Série</th>
                    <td>{nota.serie}</td>
                    <th>Número</th>
                    <td>{nota.numDoc}</td>
                  </tr>
                  <tr>
                    <th>Cliente</th>
                    <td colSpan="2">{nota.clienteId} - {nota.clienteNome}</td>
                    <th>CPF/CNPJ</th>
                    <td colSpan="2">{nota.cpf || nota.cnpj || 'Não informado'}</td>
                  </tr>
                  <tr>
                    <th>Data Lançamento</th>
                    <td>{formatDate(nota.dataLanc)}</td>
                    <th>Hora</th>
                    <td>{formatTime(nota.dataLanc)}</td>
                    <th>Situação</th>
                    <td>{getDescricaoSituacaoNF(nota.situacaoNF)}</td>
                  </tr>
                  <tr>
                    <th>Valor Produtos</th>
                    <td>{formatCurrency(nota.vProd)}</td>
                    <th>Valor Nota</th>
                    <td>{formatCurrency(nota.vNota)}</td>
                    <th>Modelo NF</th>
                    <td>{nota.modeloNF}</td>
                  </tr>
                  <tr>
                    <th>Chave</th>
                    <td colSpan="5">{nota.chave}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Itens da Nota */}
            <div className="mb-4">
              <h6>Itens</h6>
              <table className="table table-striped table-bordered table-hover">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'right' }}>Item</th>
                    <th>Produto</th>
                    <th style={{ textAlign: 'right' }}>Quantidade</th>
                    <th style={{ textAlign: 'right' }}>Valor Unitário</th>
                    <th style={{ textAlign: 'right' }}>Valor Total</th>
                  </tr>
                </thead>
                <tbody>
                  {nota.notaItens && nota.notaItens.length > 0 ? (
                    nota.notaItens.map((item) => (
                      <tr key={item.id}>
                        <td style={{ textAlign: 'right' }}>{item.item}</td>
                        <td>{item.xProd}</td>
                        <td style={{ textAlign: 'right' }}>{formatNumeric(item.qtdCom)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(item.vUnCom)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(item.vProd)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5">Nenhum item encontrado.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagamentos da Nota */}
            <div>
              <h6>Pagamentos</h6>
              <table className="table table-striped table-bordered table-hover">
                <thead>
                  <tr>
                    <th>Forma de Pagamento</th>
                    <th style={{ textAlign: 'right' }}>Valor Pago</th>
                    <th style={{ textAlign: 'right' }}>Troco</th>
                    <th>Autorização TEF</th>
                    <th>Bandeira</th>
                    <th>CNPJ Credenciadora</th>
                  </tr>
                </thead>
                <tbody>
                  {nota.notaPags && nota.notaPags.length > 0 ? (
                    nota.notaPags.map((pag) => (
                      <tr key={pag.id}>
                        <td>{pag.formaPag.descricao}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(pag.vPag)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(pag.vTroco)}</td>
                        <td>{pag.tefAutorizacao || 'N/A'}</td>
                        <td>{pag.tefBandeira || 'N/A'}</td>
                        <td>{pag.cnpjCreden}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6">Nenhum pagamento encontrado.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="modal-footer">
            {nota?.modeloNF === '65' && (
              <button
                type="button"
                className="btn btn-info"
                onClick={handleVisualizarDanfeNFCe}
              >
                Abrir PDF da NFC-e
              </button>
            )}
            <button type="button" className="btn btn-secondary" onClick={onHide}>
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
    </ModalPortal>
  );
};

export default ViewNotaModal;
